import TournamentRepository from '../repositories/tournament';
import { isToggleRuleType } from '../views/Funding/Simulation/components/Wizard/tournamentRuleUtils';

/**
 * Duplicate-competition prefill (SFB-1219).
 *
 * For ended competitions a literal server-side copy makes no sense (past dates,
 * runtime state), so instead the creation wizard is opened prefilled with the
 * source competition's core setup. This service fetches the source config
 * (GET /ext/tournaments/{id}/admin/info → TournamentSettingsWizardDTO) and maps
 * it into the store's `tournamentForm` shape consumed by every wizard step.
 *
 * Dates are intentionally left blank so the admin must pick a new schedule.
 * Not carried over (wizard limitation, acknowledged in the UI): custom email
 * templates and milestone configuration.
 */

// Same resolution the settings tabs use (RulesTab.resolveCapabilityParams):
// the platform code lives in the server-group's server details.
function resolvePlatform(settings) {
    const mtGroup = settings.metatraderServerGroupId;
    const platformGroup = settings.platformServerGroup;
    return mtGroup?.metaTraderServerDetails?.platformId?.name
        || platformGroup?.platformServerDetails?.platformId?.name
        || '';
}

function buildSelectedGroupObj(settings) {
    const mtGroup = settings.metatraderServerGroupId;
    const platformGroup = settings.platformServerGroup;
    const group = mtGroup || platformGroup;
    if (!group) return null;
    // Mirrors the wizard config's PlatformGroupDTO shape used by TournamentDetails.
    return {
        groupName: group.groupName || '',
        mtGroupId: mtGroup ? mtGroup.id : null,
        platformGroupId: !mtGroup && platformGroup ? platformGroup.id : null,
        groupNickname: ''
    };
}

function mapRules(ruleDTOs) {
    return (ruleDTOs || []).map((rule) => {
        const toggle = isToggleRuleType(rule.ruleType, null);
        return {
            type: rule.ruleType,
            value: toggle ? true : String(rule.value ?? ''),
            error: '',
            isDirty: true,
            isEditing: false
        };
    });
}

function mapAwards(awards) {
    // Shape must match TournamentAwards.addAward: the step's template reads the
    // per-field isDirty/errors UI state directly, so both must be present.
    return (awards || [])
        .slice()
        .sort((a, b) => (a.rank || 0) - (b.rank || 0))
        .map((award, index) => ({
            id: `prefill-${index + 1}`,
            position: index + 1,
            name: award.title || '',
            description: award.description || '',
            value: award.estimatedValue || '',
            isDirty: { name: false, description: false, value: false },
            errors: {}
        }));
}

export function buildTournamentFormFromAdminInfo(dto, copyName) {
    const settings = dto.tournamentSettings || {};
    const tournament = settings.tournament || {};
    const group = buildSelectedGroupObj(settings);

    return {
        basicInfo: {
            name: copyName || '',
            // Blank on purpose: the admin must choose a fresh schedule.
            startDate: '',
            endDate: '',
            startTime: '09:00',
            endTime: '18:00',
            faqsLink: settings.faqsUrl || '',
            termsCondition: settings.termsCondition || '',
            mainLanguage: settings.mainLanguage || 'en',
            selectedClubId: tournament.creator?.id ?? null
        },
        details: {
            visibility: settings.tournamentVisibility || 'PRIVATE',
            tournamentType: settings.restrictedAccountType || '',
            platform: resolvePlatform(settings),
            platformGroup: group ? group.groupName : '',
            platformGroupId: group ? group.platformGroupId : null,
            mtGroupId: group ? group.mtGroupId : null,
            selectedGroupObj: group,
            broker: 'ALL',
            selfManaged: false,
            groupNickname: '',
            publicPerformance: settings.publicPerformance ? 1 : 0,
            autoEnroll: !!settings.autoEnroll
        },
        participants: {
            rankingType: settings.tournamentRankingType || '',
            registrationPeriod: 'THREE_DAYS',
            registrationDays: settings.daysPostRegister != null ? settings.daysPostRegister : 3,
            inviteOnly: !!settings.inviteOnly
        },
        rules: mapRules(dto.tournamentRuleDTOS),
        initialBalance: settings.initialBalance != null ? String(settings.initialBalance) : '',
        awards: mapAwards(dto.awards),
        // A duplicate is a fully-specified form, so the preset step is treated
        // as already answered ("start blank" with the copied values).
        presetKey: '',
        presetSelected: true
    };
}

// Fetches the source competition's config and commits the prefilled form.
// Returns the built form (throws on request failure so callers can surface it).
export async function prefillTournamentFormFromSource(store, tournamentId, copyName) {
    const response = await TournamentRepository.getTournamentAdminInfo(tournamentId);
    const form = buildTournamentFormFromAdminInfo(response.data || {}, copyName);
    store.commit('prefillTournamentForm', form);
    // Later steps (ParticipantSettings ranking fetch, TournamentRules) read the
    // platform from this separate store object, normally written by the details
    // step on user interaction — mirror the prefilled selection there too.
    store.commit('updateTournamentPlatformInfo', {
        platform: form.details.platform,
        tournamentType: form.details.tournamentType,
        platformGroup: form.details.platformGroup,
        platformGroupId: form.details.platformGroupId,
        mtGroupId: form.details.mtGroupId,
        groupNickname: form.details.groupNickname
    });
    return form;
}
